// src/pages/Purchase/Purchase Entry/purchaseSlice.js
import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import Big from "big.js";
import {
  fetchPurchaseEntryNumber,
  createPurchaseEntry,
  fetchPurchaseRecord,
  fetchPurchaseAccs,
  fetchAssetAccs,
  fetchPendingPurchaseOrders,
} from "./purchaseThunk";
import { currentDateTime } from "../../../../lib/functions";

const initialState = {
  supplierInfo: {},
  itemCart: [],
  payPurchaseCart: [],
  pendingPOs: [],

  footerValues: {
    subtotal: "0.00",
    totalTax: "0.00",
    grandTotal: "0.00",
    total_discount: "0.00",
    description: "",
    paidAmount: 0,
    dueAmount: 0,
    serviceCharge: 0,
    discountPct: 0,
    discountAmt: 0,
    taxPct: 0,
    other_service_cost: 0,
  },

  tag_field_id: [],
  selectedPurchaseAcc: null,
  assetAccList: [],
  selectedAssetAcc: null,
  selectedDiscountAcc: null,
  selectedTaxAcc: null,
  selectedTransportAcc: null,
  conversion_rate: 0,
  conversion_currency: null,

  voucherNumber: null,
  purchase_id: null,
  pur_o_id: null,
  entryDate: currentDateTime(),
  invoiceDate: currentDateTime(),
  dueDate: null,

  selectedDetails: null,
  loading: false,
  success: null,
  taxMode: "individual",
  conversionRate: null,
  sscc_code: "",
  isTatmeen: false,
  invoiceFile: null,
  existingInvoiceUrl: null,
};

const purchaseEntrySlice = createSlice({
  name: "purchaseEntry",
  initialState,
  reducers: {
    resetPurchaseEntry() {
      return { ...initialState, supplierInfo: {}, pendingPOs: [] };
    },
    setSupplier(state, action) {
      state.supplierInfo = action.payload || {};
    },
    addOrderRow(state, action) {
      const newRow = {
        ...action.payload,
        is_tatmeen_required: false, 
        child_sscc_code: "",
        serials: [],
        serial_mode: "unit",
      };
      state.itemCart.push(newRow);
    },
    deleteOrderRow(state, action) {
      state.itemCart = state.itemCart.filter((r) => r.key !== action.payload);
    },
    updateOrderRow(state, action) {
      const { rowKey, updates } = action.payload;
      state.itemCart = state.itemCart.map((row) =>
        row.key === rowKey ? { ...row, ...updates } : row
      );
    },
    updateBatchExpiry: (state, action) => {
      const { key, batch_expiry } = action.payload;
      const row = state.itemCart.find(r => r.key === key);
      if (row) {
        row.batch_expiry = batch_expiry;
      }
    },
    setPayPurchaseCart(state, action) {
      state.payPurchaseCart = action.payload || [];
    },
    setPurchaseAcc(state, action) {
      state.selectedPurchaseAcc = action.payload;
    },
    setAssetAcc(state, action) {
      state.selectedAssetAcc = action.payload;
    },
    setDiscountAcc(state, action) {
      state.selectedDiscountAcc = action.payload;
    },
    setTaxAcc(state, action) {
      state.selectedTaxAcc = action.payload;
    },
    setTransportAcc(state, action) {
      state.selectedTransportAcc = action.payload;
    },
    setConversion(state, action) {
      state.conversion_currency = action.payload || null;
    },
    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    setInvoiceNo(state, action) {
      state.pur_invoice_no = action.payload;
    },
    setInvoiceDate(state, action) {
      state.invoiceDate = action.payload;
    },
    setTagFeildNameid(state, action) {
      state.tag_field_id = action.payload;
    },
    setIsTatmeen(state, action) {
      state.isTatmeen = action.payload;
    },
    setDueDate(state, action) {
      state.dueDate = action.payload;
    },
    setTaxMode(state, action) {
      state.taxMode = action.payload;
    },
    setConversionRate(state, action) {
      state.conversionRate = action.payload;
    },
    setFormField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
    },
    setVoucherNumber: (state, action) => {
      state.voucherNumber = action.payload;
    },
    setInvoiceFile(state, action) {
      state.invoiceFile = action.payload;
    },
    setExistingInvoiceUrl(state, action) {
      state.existingInvoiceUrl = action.payload;
    },
    setFooterValues(state, action) {
      state.footerValues = { ...state.footerValues, ...action.payload };
    },
    calculateTotals(state, action) {
      const authInfo = action?.payload?.authInfo || {};
      console.log(authInfo, "authInfo--->")
      const isIndia = authInfo?.country?.toLowerCase() === "india";
      const gstEnabled = authInfo?.gst_enabled === "yes";
      const showTax = !isIndia || gstEnabled;
      let subtotal = new Big(0);
      let totalDiscount = new Big(0);
      let totalTax = new Big(0);
      let finalSubTotal = 0;

      state.itemCart.forEach((it) => {
        const qty = new Big(it.quantity || 0);
        const rate = new Big(it.rate || 0);

        if (it.bonus) return;

        let line = qty.times(rate);

        if (state.taxMode === "individual") {
          const discountMode = it.discountMode === "amount" ? "amount" : "percent";
          let discountAmt;
          if (discountMode === "amount") {
            const rawAmt = new Big(it.discountAmt || 0);
            discountAmt = rawAmt.gt(line) ? line : rawAmt.lt(0) ? new Big(0) : rawAmt;
          } else {
            const discountPct = new Big(it.discount || 0);
            discountAmt = line.times(discountPct).div(100);
          }
          const afterDiscount = line.minus(discountAmt);

          const isGstDisabled =
            authInfo?.gst_enabled === "yes" &&
            state.supplierInfo?.gst_type === "unregistered";
          console.log(isGstDisabled, "isGstDisabled");
          console.log(authInfo?.gst_enabled, "isGstDisabled1");
          console.log(state.supplierInfo?.gst_type, "isGstDisabled12");

          const taxPer = (!showTax || isGstDisabled)
            ? new Big(0)
            : new Big(it.tax_per || 0);
          const taxAmt = afterDiscount.times(taxPer).div(100);
          line = afterDiscount.plus(taxAmt);

          subtotal = subtotal.plus(line);
          totalDiscount = totalDiscount.plus(discountAmt);
          totalTax = totalTax.plus(taxAmt);
        } else {
          subtotal = subtotal.plus(line);
        }
      });

      let resolvedDiscountPct = state.footerValues.discountPct;

      if (state.taxMode === "on_total") {
        const discountMode = state.footerValues.discountMode === "amount" ? "amount" : "percent";
        if (discountMode === "amount") {
          const rawAmt = new Big(state.footerValues.discountAmt || 0);
          totalDiscount = rawAmt.gt(subtotal) ? subtotal : rawAmt.lt(0) ? new Big(0) : rawAmt;
          // Back-computed so the printed voucher (which reads this field
          // directly) is correct even when the footer is in amount mode.
          resolvedDiscountPct = subtotal.eq(0) ? 0 : Number(totalDiscount.div(subtotal).times(100).toFixed(13));
        } else {
          const discountPct = new Big(state.footerValues.discountPct || 0);
          totalDiscount = subtotal.times(discountPct).div(100);
          resolvedDiscountPct = discountPct.toNumber();
        }

        const afterDiscount = subtotal.minus(totalDiscount);

        const taxPct = showTax ? new Big(state.footerValues.taxPct || authInfo?.item_tax || 0) : new Big(0);
        totalTax = afterDiscount.times(taxPct).div(100);
        state.footerValues.taxPct = taxPct.toNumber();
      }

      const otherServiceCost = new Big(state.footerValues.other_service_cost || 0);
      // const purchaseCharge = Number(state.footerValues.payServiceCart) || 0;
      const purchaseCharge = Array.isArray(state.footerValues.payServiceCart)
        ? state.footerValues.payServiceCart.reduce(
          (sum, r) => sum.plus(new Big(r.tran_amount || 0)),
          new Big(0)
        )
        : new Big(0);

      const paidAmount = new Big(state.footerValues.paidAmount || 0);
      const grandTotal =
        state.taxMode === "individual"
          ? subtotal.plus(purchaseCharge)  // ✅ remove .plus(otherServiceCost)
          : subtotal
            .minus(totalDiscount)
            .plus(totalTax)
            .plus(purchaseCharge);
      let due = new Big(grandTotal).minus(paidAmount);

      // to avoid dues below 0.01 values
      if (due.abs().lt(0.01)) {
        due = new Big(0);
      }

      state.footerValues = {
        ...state.footerValues, // ✅ keep discountPct, purchaseCharge, description, etc.
        subtotal: subtotal.toFixed(13),
        total_discount: totalDiscount.toFixed(13),
        discountPct: resolvedDiscountPct,
        totalTax: totalTax.toFixed(13),
        grandTotal: grandTotal.toFixed(13),
        dueAmount: due.toFixed(13),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseEntryNumber.fulfilled, (state, action) => {
        state.voucherNumber = action.payload;
        state.pur_voucher_no = action.payload;
      })
      .addCase(createPurchaseEntry.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPurchaseEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(createPurchaseEntry.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchPurchaseAccs.fulfilled, (state, action) => {
        state.PurchaseAccs = action.payload;

        if (action.payload?.length > 0 && !state.selectedPurchaseAcc) {
          state.selectedPurchaseAcc = action.payload[0]; // ✅ auto-pick first
        }
      })
      .addCase(fetchPendingPurchaseOrders.fulfilled, (state, action) => {
        state.pendingPOs = action.payload || [];
      })
      //       .addCase(fetchPurchaseRecord.fulfilled, (state, action) => {
      //   const { record, itemCart, trans } = action.payload || {};
      //   if (!record) return;

      //   // supplier info
      //   state.supplierInfo = {
      //     acc_id: record.acc_id,
      //     acc_name: record.acc_name,
      //     institution_name: record.institution_name,
      //     contact_no: record.contact_no,
      //     address: record.address,
      //     party_type: record.party_type ?? null,
      //     credit_month: record.credit_month ?? null,
      //     credit_day: record.credit_day ?? null,
      //     credit_limit: record.credit_limit ?? null,
      //   };

      //   // purchase account (✅ missing before!)
      //   state.selectedPurchaseAcc = {
      //     acc_id: record.purchase_acc_id,
      //     acc_name: record.purchase_acc_name,
      //   };

      //   // items
      //   state.itemCart = itemCart;

      //   // transactions
      //   state.payPurchaseCart = trans;

      //   // footer/master data
      //   state.footerValues = {
      //     ...state.footerValues,
      //     narration: record.narration || "",
      //     subtotal: record.sub_total,
      //     total_discount: record.total_discount,
      //     total_discount_per: record.total_discount_per,
      //     totalTax: record.total_tax,
      //     total_tax_per: record.total_tax_per,
      //     grandTotal: record.total_amount,
      //     paidAmount: record.paid_amount ?? 0,
      //     dueAmount: record.due_amount ?? 0,
      //   };

      //   // accounts
      //   state.selectedDiscountAcc = {
      //     acc_id: record.discount_acc_id,
      //     acc_name: record.discount_acc_name,
      //   };
      //   state.selectedTaxAcc = {
      //     acc_id: record.tax_acc_id,
      //     acc_name: record.tax_acc_name,
      //   };
      //   state.selectedTransportAcc = {
      //     acc_id: record.transport_acc_id,
      //     acc_name: record.transport_acc_name,
      //   };

      //   // voucher / id
      //   state.voucherNumber = record.pur_voucher_no;
      //   state.purchase_id = record.purchase_id;

      //   // entry / due date
      //   state.entryDate = record.entry_date;
      //   if (record.due_date) {
      //     const value = parseInt(record.due_date, 10) || 0;
      //     if (!isNaN(value) && value > 0) {
      //       state.dueDate = dayjs().add(value, "day").toISOString();
      //     }
      //   }
      // });

      .addCase(fetchPurchaseRecord.fulfilled, (state, action) => {
        // const { record, itemCart, trans, isPO } = action.payload;

        // state.itemCart = itemCart || [];
        // state.payPurchaseCart = trans || []; // ✅ correct field

        // if (isPO) {
        //   state.record = record; // keep PO record in state
        //   state.purchase_id = 0; // new purchase

        //   state.supplierInfo = {
        //     acc_id: record.acc_id,
        //     acc_name: record.acc_name,
        //     institution_name: record.institution_name,
        //     contact_no: record.contact_no,
        //     address: record.address,
        //     party_type: record.party_type ?? null,
        //   };

        // } else {
        //   state.purchase_id = record.pur_id;
        //   state.record = record;
        //   state.itemCart = itemCart || [];
        //   state.payPurchaseCart = trans || []; // ✅ correct field
        //   state.voucherNumber = record.pur_voucher_no;
        //   state.supplierInfo = {
        //     acc_id: record.acc_id,
        //     acc_name: record.acc_name,
        //     institution_name: record.institution_name,
        //     contact_no: record.contact_no,
        //     address: record.address,
        //     party_type: record.party_type ?? null,
        //   };
        // }

        // state.taxMode =
        //   record.is_cal_type === "individual" ? "individual" : "on_total";
        if (!action.payload) return;

        state.record = action.payload.record;
        state.itemCart = action.payload.itemCart;
        state.trans = action.payload.trans;
        state.isPO = action.payload.isPO;

        if (action.payload.isPO) {
          state.pur_o_id = action.payload.record?.pur_o_id || null;
        }

        state.supplierInfo = action.payload.supplierInfo;

        // ✅ Use footer TOTALS computed during PO load
        state.footerValues = {
          ...state.footerValues,
          ...action.payload.footerValues,
        };

        // ✅ Set tax mode
        if (action.payload.taxMode) state.taxMode = action.payload.taxMode;
      });
  },
});

export const {
  resetPurchaseEntry,
  setSupplier,
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  updateBatchExpiry,
  setPayPurchaseCart,
  setDiscountAcc,
  setTaxAcc,
  setTransportAcc,
  setPurchaseAcc,
  setAssetAcc,
  setConversion,
  setEntryDate,
  setInvoiceDate,
  setDueDate,
  setFormField,
  setFooterValues,
  calculateTotals,
  setTaxMode,
  setInvoiceNo,
  setTagFeildNameid,
  setConversionRate,
  setVoucherNumber,
  setInvoiceFile,
  setExistingInvoiceUrl,
  setIsTatmeen,
} = purchaseEntrySlice.actions;

export default purchaseEntrySlice.reducer;
